import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { Helmet } from "react-helmet-async";

const rules = [
  {
    number: 1,
    title: "NO DRUGS",
    description:
      "Any use of illegal substances are banned from the premises. There are no exceptions.",
  },
  {
    number: 2,
    title: "MUSIC, PROFANITY, & NOISE",
    description:
      "Music volume must be kept at a moderate level. We have two studios that are often being rented out at the same time. We must respect the fact that others may not like and want to hear what's being played. Music with profanity is allowed, but we ask that again, you keep the volume to levels that doesn't carry beyond the studio you're renting. Video is recorded here as well so the excessive sounds may be picked up by other users of the space.",
  },
  {
    number: 3,
    title: "NUDITY & PRIVACY",
    description:
      "Nudity Shoots are permitted, but subjects must remain inside of their studio space with doors closed at all times. We respect all forms of artistic expression, but we all respect the views of others as well. Users of the space may not want to view that or allow their children to see it as well. So, we ask that this is strongly taken into consideration when shooting as such. If using changeout rooms please come out in covering. We have robes here as well if needed.",
  },
  {
    number: 4,
    title: "SCHEDULED TIME & OVERAGES",
    description:
      "Your studio time starts at the time of your scheduled appointment not when you arrive or start shooting. If you go over your allotted scheduled time and no one is shooting directly behind you, there is an option to extend your time then and only if no one is shooting behind you. After the initial hour, times can be booked at the prorated rates for 30 minute increments if there are available slots behind your appointment slot.",
  },
  {
    number: 5,
    title: "STUDIO MAINTENANCE & CLEANING",
    description:
      "It's your responsibility to leave the studio in the condition that it was in when you arrived. If there is use of confetti, balloons, etc. it's your responsibility to clean up before wrapping up your session. Please allot enough time for clean-up.",
  },
  {
    number: 6,
    title: "PROPERTY DAMAGE",
    description:
      "Anything damaged while shooting here at the space is the responsibility of those using the space. Equipment must be replaced or paid for within the first 7 days of the accident. Legal measures will be taken if one fails to adhere.",
  },
  {
    number: 7,
    title: "WEAPONS & FIREARMS",
    description:
      "Any weapons on premises must be legally owned. We ask that all parties handling firearms assure us that none are loaded and clips are emptied prior to entering the premises.",
  },
];

const bookingPolicies = [
  {
    title: "BOOKING & PAYMENT",
    items: [
      "Full payment is due at the time of booking to confirm your session.",
      "Arrive on time — your session starts at the booked time regardless of arrival.",
      "Sessions that run over will be charged at the hourly rate in 30-minute increments.",
      "Only the booked studio space is available for use.",
    ],
  },
  {
    title: "REFUND & RETURNS POLICY",
    items: [
      "Studio bookings require a nonrefundable deposit to secure appointments. In rare cases refunds are issued by review and are determined on a case by case basis.",
      "If you're renting equipment from us and our equipment suffers failure and it's determined that the cause is something that is out of your control then a refund would be available for instances as such.",
      "Non-refundable items: Backdrop rentals, Prop rentals, Beauty Station rental.",
    ],
  },
  {
    title: "LIABILITY",
    items: [
      "MyStudio is not responsible for personal belongings left on the premises.",
      "All persons using the studio do so at their own risk.",
      "MyStudio is not liable for injuries sustained on the premises unless caused by negligence.",
      "By booking, you agree to these terms and conditions.",
    ],
  },
];

const faqEntries: Array<{ q: string; a: string }> = [
  { q: "When does my studio time start?", a: "Your studio time starts at the time of your scheduled appointment, not when you arrive or start shooting." },
  { q: "Can I extend my session if I run over?", a: "If no one is booked behind you, you can extend in 30-minute increments at the prorated hourly rate." },
  { q: "Is full payment required to book?", a: "Yes. Full payment is due at the time of booking to confirm your session." },
  { q: "What is your cancellation policy?", a: "Studio bookings require a nonrefundable deposit to secure appointments. Refunds are issued by review on a case-by-case basis." },
  { q: "Are nudity shoots allowed?", a: "Yes, but subjects must remain inside the rented studio with doors closed at all times to respect other guests using the shared space." },
  { q: "Who is responsible for property damage?", a: "Anyone using the space is responsible for damage caused while shooting. Damaged equipment must be replaced or paid for within 7 days." },
  { q: "Are weapons or firearms allowed on set?", a: "Only legally owned weapons. Firearms must be unloaded with clips emptied before entering the premises." },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqEntries.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const StudioRulesPage = () => (
  <div className="pt-20">
    <Helmet>
      <title>Studio Rules & Booking Policies | MyStudio</title>
      <meta name="description" content="MyStudio house rules, booking policies, cancellation terms, and liability information for our Columbus, GA studio rental space." />
      <link rel="canonical" href="https://bookmystudiospace.com/studio-rules" />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
    </Helmet>
    <section className="py-20 text-center">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-4">STUDIO RULES</h1>
        <p className="text-muted-foreground font-body text-lg">Everyone follow these MyStudio Rules</p>
      </div>
    </section>

    <section className="pb-12">
      <div className="container mx-auto px-4 max-w-3xl space-y-6">
        {rules.map((rule) => (
          <div key={rule.number} className="bg-panel border border-border p-6">
            <h2 className="text-xl font-display tracking-widest text-primary mb-3">
              {rule.number}. {rule.title}
            </h2>
            <p className="text-sm text-foreground/80 font-body leading-relaxed">
              {rule.description}
            </p>
          </div>
        ))}

        <div className="bg-panel border border-primary/30 p-6 text-center">
          <h2 className="text-xl font-display tracking-widest text-primary mb-3">THANK YOU!</h2>
          <p className="text-sm text-foreground/80 font-body">
            Contact us at{" "}
            <a href="mailto:info@bookmystudiospace.com" className="text-primary hover:text-primary/80 transition-colors">
              info@bookmystudiospace.com
            </a>{" "}
            for questions related to any of the above or refunds and returns policy.
          </p>
        </div>
      </div>
    </section>

    <section className="pb-20">
      <div className="container mx-auto px-4 max-w-3xl space-y-6">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-center mb-8">
          BOOKING & CANCELLATION POLICIES
        </h2>
        {bookingPolicies.map((section) => (
          <div key={section.title} className="bg-panel border border-border p-6">
            <h3 className="text-xl font-display tracking-widest text-primary mb-4">{section.title}</h3>
            <ul className="space-y-2">
              {section.items.map((item, i) => (
                <li key={i} className="text-sm text-foreground/80 font-body pl-4 border-l-2 border-border">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
          <Button variant="outline" className="border-border text-muted-foreground hover:text-foreground font-body px-6">
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
          <Link to="/book">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-8">
              I Understand — Book Now
            </Button>
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default StudioRulesPage;
